import { APIClient } from "../helpers/api_helper";
import { ApiResponse } from "../types/api";

const api = new APIClient();

export type ClinicalMapType = "face" | "body";

export interface ClinicalMapRegion {
  id: number;
  tenant_id: number;
  map_type: ClinicalMapType;
  code: string;
  name: string;
  sort_order: number;
  hotspot_x_pct: number | null;
  hotspot_y_pct: number | null;
  has_hotspot: boolean;
  is_active: boolean;
  is_system: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ClinicalProtocolMarking {
  id: number;
  tenant_id: number;
  clinical_protocol_id: number;
  clinical_protocol_session_id: number | null;
  clinical_map_region_id: number;
  product_id: number | null;
  product_label: string | null;
  quantity_x100: number;
  notes: string | null;
  recorded_at: string;
  recorded_by_user_id: number | null;
  region?: {
    id: number;
    map_type: ClinicalMapType;
    code: string;
    name: string;
    hotspot_x_pct: number | null;
    hotspot_y_pct: number | null;
    is_active: boolean;
  } | null;
  product?: {
    id: number;
    name: string;
    unit_of_measure: string;
  } | null;
  session?: {
    id: number;
    session_number: number;
    status: string;
  } | null;
  recorded_by?: {
    id: number;
    name: string;
  } | null;
}

export interface CreateClinicalMapRegionPayload {
  map_type: ClinicalMapType;
  name: string;
  code?: string | null;
  sort_order?: number | null;
  hotspot_x_pct?: number | null;
  hotspot_y_pct?: number | null;
  is_active?: boolean;
}

export interface UpdateClinicalMapRegionPayload {
  name?: string;
  sort_order?: number | null;
  hotspot_x_pct?: number | null;
  hotspot_y_pct?: number | null;
  is_active?: boolean;
}

export interface UpsertClinicalProtocolMarkingPayload {
  clinical_map_region_id: number;
  clinical_protocol_session_id?: number | null;
  product_id?: number | null;
  product_label?: string | null;
  quantity_x100?: number;
  notes?: string | null;
  recorded_at?: string | null;
}

export const quantityToDisplay = (quantityX100: number): string => {
  return (Number(quantityX100) / 100).toFixed(2).replace(/\.?0+$/, "");
};

export const displayToQuantityX100 = (raw: string): number | null => {
  const normalized = raw.trim().replace(",", ".");
  if (normalized === "") {
    return null;
  }
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return Math.round(parsed * 100);
};

export const listClinicalMapRegions = (params: {
  map_type?: ClinicalMapType;
  include_inactive?: boolean;
  is_active?: boolean;
} = {}): Promise<ApiResponse<ClinicalMapRegion[]>> =>
  api.get<ApiResponse<ClinicalMapRegion[]>>("/clinical-map-regions", params);

export const createClinicalMapRegion = (
  payload: CreateClinicalMapRegionPayload
): Promise<ApiResponse<ClinicalMapRegion>> =>
  api.create<ApiResponse<ClinicalMapRegion>>("/clinical-map-regions", payload);

export const updateClinicalMapRegion = (
  id: number,
  payload: UpdateClinicalMapRegionPayload
): Promise<ApiResponse<ClinicalMapRegion>> =>
  api.update<ApiResponse<ClinicalMapRegion>>(`/clinical-map-regions/${id}`, payload);

export const deactivateClinicalMapRegion = (
  id: number
): Promise<ApiResponse<ClinicalMapRegion>> =>
  api.delete<ApiResponse<ClinicalMapRegion>>(`/clinical-map-regions/${id}`);

export const listProtocolMarkings = (
  protocolId: number,
  params: {
    session_id?: number;
    region_id?: number;
    map_type?: ClinicalMapType;
  } = {}
): Promise<ApiResponse<ClinicalProtocolMarking[]>> =>
  api.get<ApiResponse<ClinicalProtocolMarking[]>>(
    `/clinical-protocols/${protocolId}/markings`,
    params
  );

export const createProtocolMarking = (
  protocolId: number,
  payload: UpsertClinicalProtocolMarkingPayload
): Promise<ApiResponse<ClinicalProtocolMarking>> =>
  api.create<ApiResponse<ClinicalProtocolMarking>>(
    `/clinical-protocols/${protocolId}/markings`,
    payload
  );

export const updateProtocolMarking = (
  protocolId: number,
  markingId: number,
  payload: Partial<UpsertClinicalProtocolMarkingPayload>
): Promise<ApiResponse<ClinicalProtocolMarking>> =>
  api.update<ApiResponse<ClinicalProtocolMarking>>(
    `/clinical-protocols/${protocolId}/markings/${markingId}`,
    payload
  );

export const deleteProtocolMarking = (
  protocolId: number,
  markingId: number
): Promise<ApiResponse<null>> =>
  api.delete<ApiResponse<null>>(
    `/clinical-protocols/${protocolId}/markings/${markingId}`
  );
